import React, { useState } from "react";
import {
  CheckCircle2,
  Circle,
  ChevronDown,
  ChevronRight,
  Plus,
  X,
  Edit3,
  Save,
} from "lucide-react";

import { todosData } from "./todosData";

const TodoList = () => {
  const [todos, setTodos] = useState(todosData);
  const [newItemText, setNewItemText] = useState("");
  const [editingItem, setEditingItem] = useState(null);
  const [editText, setEditText] = useState("");

  // Store expanded state for any item by its id
  const [expandedMap, setExpandedMap] = useState(() =>
    // Initialize with phases expanded state if provided
    todosData.reduce((acc, todo) => {
      acc[todo.id] = todo.expanded ?? false;
      // Also initialize nested items collapsed by default
      if (todo.items) {
        todo.items.forEach((item) => {
          acc[item.id] = item.expanded ?? false;
          if (item.items) {
            item.items.forEach((subItem) => {
              acc[subItem.id] = subItem.expanded ?? false;
            });
          }
        });
      }
      return acc;
    }, {})
  );

  // Calculate completion percentage for items recursively
  const calculateCompletion = (items) => {
    if (!items || items.length === 0) return 0;
    const totalCount = items.length;
    const completedCount = items.filter((item) => item.completed).length;
    return Math.round((completedCount / totalCount) * 100);
  };

  const toggleTodoCompleted = (id, parentIds = []) => {
    setTodos((prevTodos) =>
      prevTodos.map((todo) => {
        if (todo.id === id && parentIds.length === 0) {
          return { ...todo, completed: !todo.completed };
        }
        if (parentIds.length === 1 && todo.id === parentIds[0]) {
          const updatedItems = todo.items.map((item) =>
            item.id === id ? { ...item, completed: !item.completed } : item
          );
          return { ...todo, items: updatedItems };
        }
        if (parentIds.length === 2 && todo.id === parentIds[0]) {
          const updatedItems = todo.items.map((item) => {
            if (item.id === parentIds[1] && item.items) {
              const updatedSubItems = item.items.map((subItem) =>
                subItem.id === id
                  ? { ...subItem, completed: !subItem.completed }
                  : subItem
              );
              return { ...item, items: updatedSubItems };
            }
            return item;
          });
          return { ...todo, items: updatedItems };
        }
        return todo;
      })
    );
  };

  const toggleExpanded = (id) => {
    setExpandedMap((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const addNewItem = (parentId, grandParentId = null) => {
    if (!newItemText.trim()) return;

    const newItem = {
      id: Date.now().toString(),
      title: newItemText,
      completed: false,
    };

    setTodos((prevTodos) =>
      prevTodos.map((todo) => {
        if (!grandParentId && todo.id === parentId) {
          // Adding a task to phase
          return { ...todo, items: [...(todo.items || []), newItem] };
        }
        if (grandParentId && todo.id === grandParentId) {
          // Adding a subtask to task
          const updatedItems = todo.items.map((item) => {
            if (item.id === parentId) {
              return {
                ...item,
                items: item.items ? [...item.items, newItem] : [newItem],
              };
            }
            return item;
          });
          return { ...todo, items: updatedItems };
        }
        return todo;
      })
    );

    setNewItemText("");
  };

  const startEdit = (id) => {
    setEditingItem(id);
    // Find item title from todos for editText initialization
    const findTitle = (arr) => {
      for (const item of arr) {
        if (item.id === id) return item.title;
        if (item.items) {
          const subTitle = findTitle(item.items);
          if (subTitle) return subTitle;
        }
      }
      return null;
    };
    const title = findTitle(todos);
    setEditText(title || "");
  };

  const saveEdit = () => {
    if (!editText.trim()) return;

    const updateTitle = (items) =>
      items.map((item) => {
        if (item.id === editingItem) {
          return { ...item, title: editText };
        }
        if (item.items) {
          return { ...item, items: updateTitle(item.items) };
        }
        return item;
      });

    setTodos((prevTodos) => updateTitle(prevTodos));

    setEditingItem(null);
    setEditText("");
  };

  const deleteItem = (id, parentId = null, grandParentId = null) => {
    setTodos((prevTodos) =>
      prevTodos
        .map((todo) => {
          if (!parentId && todo.id === id) {
            // Deleting a phase (if ever needed)
            return null;
          }
          if (!grandParentId && todo.id === parentId) {
            // Delete task from phase
            return {
              ...todo,
              items: todo.items.filter((item) => item.id !== id),
            };
          }
          if (grandParentId && todo.id === grandParentId) {
            // Delete subtask from task
            const updatedItems = todo.items.map((item) => {
              if (item.id === parentId && item.items) {
                return {
                  ...item,
                  items: item.items.filter((subItem) => subItem.id !== id),
                };
              }
              return item;
            });
            return { ...todo, items: updatedItems };
          }
          return todo;
        })
        .filter(Boolean)
    );
  };

  const renderImages = (images) =>
    images?.length > 0 && (
      <div className="o-row o-row--gap-size_300" style={{ marginBottom: 12 }}>
        {images.map((src, idx) => (
          <img key={idx} src={src} alt="todo" className="todo-image" />
        ))}
      </div>
    );

  const renderTables = (tables) =>
    tables?.length > 0 && (
      <div className="tables-container" style={{ marginBottom: 12 }}>
        {tables.map((table, tIdx) => (
          <table key={tIdx} className="todo-table">
            <tbody>
              {table.map((row, rIdx) => (
                <tr key={rIdx}>
                  {row.map((cell, cIdx) => (
                    <td key={cIdx}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        ))}
      </div>
    );

  // Recursive component to render any level of items
  const renderItems = (items, parentIds = []) => {
    if (!items) return null;

    return items.map((item) => {
      const isExpanded = expandedMap[item.id] ?? false;
      const completion = calculateCompletion(item.items);

      return (
        <div
          key={item.id}
          className="task-group"
          style={{ marginLeft: parentIds.length * 20 }}
        >
          <div
            className="task-item u-width_full-width"
            style={{ flexDirection: "column", alignItems: "flex-start" }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                gap: 8,
                cursor: "pointer",
                userSelect: "none",
              }}
              onClick={() => toggleExpanded(item.id)}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                {isExpanded ? (
                  <ChevronDown size={20} />
                ) : (
                  <ChevronRight size={20} />
                )}
              </div>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toggleTodoCompleted(item.id, parentIds);
                }}
                className={`task-checkbox`}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                {item.completed ? (
                  <CheckCircle2 size={20} className="checkbox-completed" />
                ) : (
                  <Circle size={20} className="checkbox-empty" />
                )}
              </button>

              {editingItem === item.id ? (
                <div className="edit-container" style={{ flex: 1 }}>
                  <input
                    type="text"
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    className={`edit-input ${
                      parentIds.length === 2 ? "small" : ""
                    }`}
                    onKeyPress={(e) => e.key === "Enter" && saveEdit()}
                    style={{ flex: 1 }}
                  />
                  <button
                    onClick={saveEdit}
                    className={`edit-save-btn ${
                      parentIds.length === 2 ? "small" : ""
                    }`}
                  >
                    <Save size={parentIds.length === 2 ? 14 : 16} />
                  </button>
                  <button
                    onClick={() => setEditingItem(null)}
                    className={`edit-cancel-btn ${
                      parentIds.length === 2 ? "small" : ""
                    }`}
                  >
                    <X size={parentIds.length === 2 ? 14 : 16} />
                  </button>
                </div>
              ) : (
                <span
                  className={`task-title ${item.completed ? "completed" : ""}`}
                  style={{ flex: 1 }}
                >
                  {item.title}
                </span>
              )}

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  startEdit(item.id);
                }}
                className={`action-btn edit-btn ${
                  parentIds.length === 2 ? "small" : ""
                }`}
                title="Edit"
              >
                <Edit3 size={parentIds.length === 2 ? 14 : 16} />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  deleteItem(item.id, parentIds[0], parentIds[1]);
                }}
                className={`action-btn delete-btn ${
                  parentIds.length === 2 ? "small" : ""
                }`}
                title="Delete"
              >
                <X size={parentIds.length === 2 ? 14 : 16} />
              </button>
            </div>

            {isExpanded && (
              <div className="u-padding--left_700 u-padding--right_700 u-width_full-width">
                {item.description && (
                  <p
                    className={`${
                      parentIds.length === 2
                        ? "subtask-description"
                        : "task-description"
                    }`}
                  >
                    {item.description}
                  </p>
                )}
                {renderImages(item.images)}
                {renderTables(item.tables)}

                {item.items && renderItems(item.items, [...parentIds, item.id])}

                <div
                  className={
                    parentIds.length === 1 ? "add-subtask" : "add-task"
                  }
                  style={{ marginTop: 8 }}
                >
                  <input
                    type="text"
                    value={newItemText}
                    onChange={(e) => setNewItemText(e.target.value)}
                    placeholder={
                      parentIds.length === 1
                        ? "Add new sub-task..."
                        : "Add new task..."
                    }
                    className={`add-input ${
                      parentIds.length === 1 ? "small" : ""
                    }`}
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        if (parentIds.length === 1)
                          addNewItem(item.id, parentIds[0]);
                        else addNewItem(item.id);
                      }
                    }}
                  />
                  <button
                    onClick={() => {
                      if (parentIds.length === 1)
                        addNewItem(item.id, parentIds[0]);
                      else addNewItem(item.id);
                    }}
                    className={`add-btn ${
                      parentIds.length === 1 ? "small" : "primary"
                    }`}
                  >
                    <Plus size={parentIds.length === 1 ? 16 : 20} />
                    {parentIds.length === 0 && <span>Add Task</span>}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      );
    });
  };

  return (
    <div className="todo-app">
      <div className="todo-container">
        <div className="todo-card">
          <div className="todo-header">
            <h1 className="todo-title">Al-Habbar Technologies</h1>
            <p className="todo-subtitle">
              Business Plan Implementation Tracker
            </p>
          </div>

          <div className="todo-content">
            {todos.map((phase) => {
              const completion = calculateCompletion(phase.items);

              const isExpanded = expandedMap[phase.id] ?? false;

              return (
                <div key={phase.id} className="phase-section">
                  <div
                    className="phase-header"
                    onClick={() => toggleExpanded(phase.id)}
                  >
                    <div className="phase-header-left">
                      <div className="expand-icon">
                        {isExpanded ? (
                          <ChevronDown size={20} />
                        ) : (
                          <ChevronRight size={20} />
                        )}
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleTodoCompleted(phase.id);
                        }}
                        className="phase-checkbox"
                      >
                        {phase.completed ? (
                          <CheckCircle2
                            size={24}
                            className="checkbox-completed"
                          />
                        ) : (
                          <Circle size={24} className="checkbox-empty" />
                        )}
                      </button>
                      <h2
                        className={`phase-title ${
                          phase.completed ? "completed" : ""
                        }`}
                      >
                        {phase.title}
                      </h2>
                    </div>
                    <div className="phase-header-right">
                      <div className="completion-text">
                        {completion}% complete
                      </div>
                      <div className="progress-bar-container">
                        <div
                          className="progress-bar-fill"
                          style={{ width: `${completion}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="phase-content">
                      <div className="u-padding--left_500 u-width_full-width">
                        {phase.description && (
                          <p className="todo-description u-padding--left_200 u-padding--right_200">
                            {phase.description}
                          </p>
                        )}
                        <div className="u-padding--left_200 u-padding--right_200">
                          {renderImages(phase.images)}
                        </div>
                        {renderTables(phase.tables)}
                      </div>
                      {renderItems(phase.items, [phase.id])}

                      <div className="add-task" style={{ marginTop: 12 }}>
                        <input
                          type="text"
                          value={newItemText}
                          onChange={(e) => setNewItemText(e.target.value)}
                          placeholder="Add new task..."
                          className="add-input"
                          onKeyPress={(e) =>
                            e.key === "Enter" && addNewItem(phase.id)
                          }
                        />
                        <button
                          onClick={() => addNewItem(phase.id)}
                          className="add-btn primary"
                        >
                          <Plus size={16} />
                          <span>Add Task</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TodoList;
